import { useEffect } from "react";
import { getApiUrl } from "@/config/environment";
import { FindifyDataContextType } from "@/contexts/FindifyDataContext";

const sentPageViews = new Set<string>();

const getContentIdFromParams = (params: any) => (
    params?.content_id
    ?? params?.point_of_interest_id
    ?? params?.route_id
    ?? params?.event_id
    ?? params?.page_id
);

const getModuleFromRoute = (routeName?: string, params?: any) => {
    if (params?.module) return params.module;
    if (params?.content_type) return params.content_type;

    switch (routeName) {
        case "Place":
            return "home";
        case "PlantsPage":
        case "PlaceMap":
            return "map";
        case "PlaceContentDetails":
            return "contents";
        case "PlaceContentList":
            return params?.module ?? "contents";
        default:
            return routeName;
    }
}

const makeStatisticsRoute = (routeName?: string, params?: any) => {
    const moduleName = getModuleFromRoute(routeName, params);
    const contentId = getContentIdFromParams(params);

    if (routeName === "PlaceContentDetails" && moduleName && contentId) {
        return `${moduleName}/${contentId}`;
    }

    if (routeName === "PlaceContentList") {
        const category = params?.category ?? params?.category_id;
        return category ? `${moduleName}/list/${category}` : `${moduleName}/list`;
    }

    return routeName ?? moduleName ?? "unknown";
}

export const useStatisticsPageView = ({
    findifyDataContext,
    routeName,
    routeParams,
    enabled = true,
}: {
    findifyDataContext: FindifyDataContextType | null,
    routeName?: string,
    routeParams?: any,
    enabled?: boolean,
}) => {
    useEffect(() => {
        const workspaceId = Number(findifyDataContext?.workspace?.workspace_id);
        if (!enabled || !workspaceId || !Number.isFinite(workspaceId)) return;

        const moduleName = getModuleFromRoute(routeName, routeParams);
        const contentId = getContentIdFromParams(routeParams);
        const route = makeStatisticsRoute(routeName, routeParams);
        const language = findifyDataContext?.language;
        const dedupeKey = `${workspaceId}:${language ?? ""}:${route}:${moduleName ?? ""}:${contentId ?? ""}`;

        if (sentPageViews.has(dedupeKey)) return;
        sentPageViews.add(dedupeKey);

        fetch(`${getApiUrl()}/statistics/page_view`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                source: "app",
                workspace_id: workspaceId,
                route,
                module: moduleName,
                content_id: contentId !== undefined && contentId !== null ? String(contentId) : undefined,
                language,
            }),
            keepalive: true,
        }).catch(() => {
            sentPageViews.delete(dedupeKey);
        });
    }, [
        enabled,
        findifyDataContext?.workspace?.workspace_id,
        findifyDataContext?.language,
        routeName,
        JSON.stringify(routeParams ?? {}),
    ]);
}
